import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export interface HeroSlide {
  id: string;
  title: string;
  summary: string;
  cover: string;
  tag: string;
  author: string;
  publishedAt: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const total = slides.length;

  const go = useCallback(
    (next: number) => setIndex(((next % total) + total) % total),
    [total],
  );

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % total), 5500);
    return () => window.clearInterval(timer);
  }, [total]);

  if (total === 0) return null;

  return (
    <section className="relative w-full h-[440px] md:h-[560px] overflow-hidden rounded-xl bg-secondary-900" aria-label="焦点内容轮播">
      {slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img
            src={slide.cover}
            alt={slide.title}
            title={`${slide.title} - 一起艹`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-secondary-950/85 via-secondary-950/40 to-secondary-950/10" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full max-w-[1280px] mx-auto px-5 md:px-10 pb-10 md:pb-14">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500 text-accent-950 text-xs font-medium mb-4">
                <i className="ri-fire-line" />
                {slide.tag}
              </span>
              <h2 className="font-heading text-2xl md:text-4xl lg:text-5xl text-background-50 max-w-3xl leading-tight">
                {slide.title}
              </h2>
              <p className="mt-3 text-sm md:text-base text-background-200 max-w-2xl line-clamp-2">{slide.summary}</p>
              <div className="mt-5 flex items-center flex-wrap gap-4">
                <Link
                  to={`/detail/${slide.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 whitespace-nowrap cursor-pointer transition-colors"
                >
                  查看详情
                  <i className="ri-arrow-right-line" />
                </Link>
                <span className="text-xs text-background-300 flex items-center gap-3">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-3-line" />
                    {slide.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" />
                    {slide.publishedAt}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {total > 1 ? (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            className="hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 hover:bg-primary-500 cursor-pointer backdrop-blur-sm transition-colors"
            aria-label="上一张"
          >
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            className="hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-secondary-950/40 text-background-50 hover:bg-primary-500 cursor-pointer backdrop-blur-sm transition-colors"
            aria-label="下一张"
          >
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>

          <div className="absolute bottom-4 right-5 md:right-10 flex items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => go(i)}
                className={`h-1.5 rounded-full cursor-pointer transition-all ${
                  i === index ? 'w-7 bg-primary-500' : 'w-4 bg-background-50/50 hover:bg-background-50/80'
                }`}
                aria-label={`切换到第 ${i + 1} 张`}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}